import {
  InnerContent,
  SimpleModal,
  SimpleModalHeader,
  SimpleModalHeaderIcon,
  SimpleModalTitle,
  StatusLabel,
} from '@fountain/fountain-ui-components';
import { Collapse, Grid, Link } from '@material-ui/core';
import ExpandLessOutlined from '@material-ui/icons/ExpandLessOutlined';
import ExpandMoreOutlined from '@material-ui/icons/ExpandMoreOutlined';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import ComplianceRequirementsTooltip from 'components/ComplianceRequirementsTooltip';
import PartnerDetailsModal from 'components/PartnerDetailsModal';
import {
  makeSelectEnvVars,
  makeSelectImmediateHiringDecision,
  makeSelectWhoami,
} from 'containers/Auth_old/selectors';
import { addMessageAction } from 'containers/FlashMessage/actions';
import useApplicantDetail from 'hooks/useApplicantDetail';
import InfoBlueIcon from 'images/info-blue.svg';
import { getWxJwt } from 'utils/wxJwtToken';

import useHandleHttpAction from './hooks';
import messages from './messages';
import useStyles from './styles';
import { statusToStatusLabelProps } from './utils';
/**
 * @typedef {object} StatusLabelsProps
 * @property {Applicant} applicant
 */

// Mirrors the contract in wx-system's
// frontends/wx-ui/src/utils/employer/iframeAuthHandoff.ts. A WX page loaded
// in this iframe has its own, browser-partitioned storage that never
// received a login even when the recruiter is already authenticated into
// WX elsewhere — it pings this window on load asking for a session to
// adopt, and we hand over the WX JWT Hire already cached for the Navbar/
// Copilot widgets. Only honored for actions the backend explicitly marks
// `wx_iframe_auth: true` (see the useEffect below) — `type: 'modal'` is
// also used for non-WX partner iframes, which must never receive this
// token.
const WX_IFRAME_AUTH_REQUEST_SOURCE = 'wx-iframe-auth:request';
const WX_IFRAME_AUTH_RESPONSE_SOURCE = 'wx-iframe-auth:response';

/**
 * @param {StatusLabelsProps} props
 * @return {JSX.Element}
 */
function StatusLabels({
  applicant,
  openStatusLabelMenu = () => {},
  displayCollapseSection = false,
  collapsed,
  updateCollapsedStatus,
}) {
  const [modalAction, setModalAction] = useState(null);
  const iframeRef = useRef(null);
  const styles = useStyles();
  const { status: statuses, partner_label: partnerLabels } = applicant;
  const applicantId =
    applicant.id ||
    (applicant.status &&
      applicant.status[0] &&
      applicant.status[0].external_id);
  const intl = useIntl();
  const dispatch = useDispatch();
  const envVars = useSelector(makeSelectEnvVars());
  const { fetchData: fetchApplicantDetail } = useApplicantDetail(
    applicantId,
    false,
  );
  const handleHttpAction = useHandleHttpAction();
  const [partnerDetails, setPartnerDetails] = useState([]);
  const handleClosePartnerDetailsModal = () => {
    setPartnerDetails([]);
  };
  const immediateHiringDecision = useSelector(
    makeSelectImmediateHiringDecision(),
  );
  const whoami = useSelector(makeSelectWhoami());

  useEffect(() => {
    // `type: 'modal'` actions can also point at non-WX partner iframes, so
    // only respond to the handshake when the backend has explicitly marked
    // this action as a trusted WX embed — never infer trust from the modal
    // URL alone.
    if (!modalAction || !modalAction.wx_iframe_auth) return undefined;

    let targetOrigin;
    try {
      targetOrigin = new URL(modalAction.url).origin;
    } catch (e) {
      return undefined;
    }

    const onMessage = event => {
      if (event.origin !== targetOrigin) return;
      if (
        !iframeRef.current ||
        event.source !== iframeRef.current.contentWindow
      ) {
        return;
      }
      if (event.data?.source !== WX_IFRAME_AUTH_REQUEST_SOURCE) return;

      const token = getWxJwt();
      if (!token) return;

      event.source.postMessage(
        { source: WX_IFRAME_AUTH_RESPONSE_SOURCE, token },
        targetOrigin,
      );
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [modalAction]);

  const toggleCollapse = () => {
    const newCollapsed = !collapsed;
    updateCollapsedStatus(applicantId, newCollapsed);
  };
  const allStatuses = [...statuses, ...(partnerLabels || [])].filter(
    s => s.status,
  );
  allStatuses.sort((a, b) => {
    const ta = a && a.created_at ? new Date(a.created_at).getTime() : 0;
    const tb = b && b.created_at ? new Date(b.created_at).getTime() : 0;
    return tb - ta;
  });
  const MAX_VISIBLE_LABELS = 3;

  const renderStatus = status => {
    let statusLabelProps;
    try {
      if (!status.status) {
        return null;
      }
      const handleOpenPartnerDetailsModal = details => {
        const filteredDetails = details.filter(
          detail =>
            detail.partner?.name === status.type && detail.key && detail.value,
        );
        setPartnerDetails(filteredDetails);
      };
      statusLabelProps = statusToStatusLabelProps(
        status,
        {
          handleHttpAction,
          dispatch,
          envVars,
          fetchApplicantDetail,
          intl,
          handleOpenPartnerDetailsModal,
          setModalAction,
        },
        immediateHiringDecision,
      );
      const foundDetails = status?.actions
        ?.find(st => st.label === 'Partner Details')
        ?.partner_details.filter(detail => detail.key && detail.value);

      const hidePartnerDetailsAction = !foundDetails?.find(
        detail => detail.partner.name === status.type,
      );
      if (hidePartnerDetailsAction) {
        statusLabelProps.actions = statusLabelProps.actions.filter(
          prop => prop.label !== 'Partner Details',
        );
      }
    } catch (e) {
      dispatch(addMessageAction(e, 'error'));
      return null;
    }
    const isModalOpen = !!modalAction;

    return (
      <Grid key={statusLabelProps.key} onClick={openStatusLabelMenu}>
        <ComplianceRequirementsTooltip requirements={status.requirements || []}>
          <StatusLabel {...statusLabelProps} />
        </ComplianceRequirementsTooltip>
        {partnerDetails.length > 0 && (
          <PartnerDetailsModal
            partnerDetails={partnerDetails}
            handleClose={handleClosePartnerDetailsModal}
            title={`${partnerDetails[0].partner?.name} Details`}
          />
        )}
        {isModalOpen && modalAction && (
          <SimpleModal
            fullScreenOnMobile
            onClose={() => setModalAction(null)}
            maxWidth="90dvw"
            height="90dvh"
          >
            <SimpleModalHeader onClose={() => setModalAction(null)}>
              <SimpleModalHeaderIcon Icon={InfoBlueIcon} color="blue" />
              <SimpleModalTitle>{modalAction.label}</SimpleModalTitle>
            </SimpleModalHeader>

            <InnerContent height="100%">
              <iframe
                ref={iframeRef}
                style={{ height: '100%', width: '100%' }}
                src={modalAction.url}
                title="embed"
                allow="camera; microphone; geolocation"
              />
            </InnerContent>
          </SimpleModal>
        )}
      </Grid>
    );
  };

  const firstStatus = allStatuses[0];
  if (
    whoami.feature_flags['collapse-applicant-rows'] &&
    displayCollapseSection
  ) {
    return (
      <Grid className={styles.statusLabels}>
        {allStatuses.length <= MAX_VISIBLE_LABELS ? (
          allStatuses.map(status => renderStatus(status))
        ) : (
          <>
            {firstStatus && renderStatus(firstStatus)}
            {allStatuses.slice(1).length > 0 && (
              <>
                <Collapse
                  in={!collapsed}
                  collapsedHeight={0}
                  classes={{
                    wrapperInner: styles.remainingStatusLabels,
                  }}
                >
                  {allStatuses.slice(1).map(status => renderStatus(status))}
                </Collapse>
                <Grid xs={12} style={{ textAlign: 'left' }}>
                  {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                  <Link
                    href="#"
                    onClick={e => {
                      e.preventDefault();
                      toggleCollapse();
                    }}
                  >
                    {!collapsed ? (
                      <>
                        <FormattedMessage {...messages.collapseStatusLabel} />
                        <ExpandLessOutlined
                          className={styles.collapseExpandIcon}
                        />
                      </>
                    ) : (
                      <>
                        <FormattedMessage
                          {...messages.expandStatusLabel}
                          values={{
                            count: allStatuses.length,
                          }}
                        />
                        <ExpandMoreOutlined
                          className={styles.collapseExpandIcon}
                        />
                      </>
                    )}
                  </Link>
                </Grid>
              </>
            )}
          </>
        )}
      </Grid>
    );
  }
  return (
    <Grid className={styles.statusLabels}>
      {[...statuses, ...(partnerLabels || [])].map(status =>
        renderStatus(status),
      )}
    </Grid>
  );
}

StatusLabels.propTypes = {
  applicant: PropTypes.object.isRequired,
  openStatusLabelMenu: PropTypes.func,
  displayCollapseSection: PropTypes.bool,
  collapsed: PropTypes.bool,
  updateCollapsedStatus: PropTypes.func,
};

export default StatusLabels;
